import React, { useState, useContext, Component } from 'react';
import { BarChart3Icon, UsersIcon, TableIcon, FileTextIcon, GlobeIcon, DollarSignIcon, PackageIcon, UmbrellaIcon, MessagesSquareIcon, ClockIcon, BuildingIcon, TruckIcon, CreditCardIcon, ListIcon, TentIcon, QrCodeIcon, SettingsIcon, ShieldIcon, SearchIcon, BellIcon, UserIcon, WarehouseIcon, ArrowRightIcon, ChevronDownIcon, ChevronRightIcon, HomeIcon, NetworkIcon, TrendingUpIcon } from 'lucide-react';
const MODULE_LABELS: Record<string, string> = {
  dashboard: 'Administrative Configurator',
  matrix: 'Matrix',
  items: 'Items',
  span: 'Span Config',
  overhang: 'Overhang Config',
  sprinkler: 'Sprinkler Config',
  regions: 'Region and Countries',
  territory: 'Territory Management',
  incoterm: 'Incoterm',
  pricelist: 'Price List',
  warehouse: 'Warehouse',
  payment: 'Payment Terms',
  transportation: 'Type Of Transportation',
  fairs: 'Agricultural Fairs',
  qrcode: 'QR Code Generator',
  version: 'Version Management',
  cron: 'Cron Tasks',
  feedback: 'Feedback',
  icontrol: 'iControl Remote',
  users: 'Users'
};
function moduleLabel(module: string) {
  return MODULE_LABELS[module] || module.charAt(0).toUpperCase() + module.slice(1);
}
import { FairsManagement } from '../components/admin/FairsManagement';
import { QRCodeGenerator } from '../components/admin/QRCodeGenerator';
import { UsersManagement } from '../components/admin/UsersManagement';
import { TerritoryManagement } from '../components/admin/TerritoryManagement';
import { FairContext } from '../contexts/FairContext';
export function AdminPage() {
  const [activeModule, setActiveModule] = useState('dashboard');
  const [searchQuery, setSearchQuery] = useState('');
  const [showNotifications, setShowNotifications] = useState(false);
  const [currentFair, setCurrentFair] = useState(null);
  const [isGeneratingQR, setIsGeneratingQR] = useState(false);
  const {
    setActiveFair
  } = useContext(FairContext);
  const handleModuleChange = module => {
    setActiveModule(module);
  };
  const handleGenerateQR = fair => {
    setCurrentFair(fair);
    setIsGeneratingQR(true);
    setActiveModule('qrcode');
  };
  const handleQRGenerated = fairId => {
    // When QR is generated successfully, update the fair to have QR
    const updatedFair = {
      ...currentFair,
      hasQRCode: true
    };
    // Set as active fair to trigger the announcement on home page
    setActiveFair(updatedFair);
    // Log to confirm the context is updated
    console.log('QR generated for fair:', updatedFair);
    setIsGeneratingQR(false);
    setCurrentFair(null);
    setActiveModule('fairs');
  };
  const renderModuleContent = () => {
    switch (activeModule) {
      case 'fairs':
        return <FairsManagement onGenerateQR={handleGenerateQR} data-id="element-7043" data-mp="1xas3c4gejyqg" />;
      case 'qrcode':
        return isGeneratingQR && currentFair ? <QRCodeGenerator fair={currentFair} onGenerated={handleQRGenerated} onCancel={() => {
          setIsGeneratingQR(false);
          setCurrentFair(null);
          setActiveModule('fairs');
        }} data-id="element-7044" data-mp="19kpgq3v9bkz8" /> : <div className="flex items-center justify-center h-64 bg-white/5 backdrop-blur-sm rounded-xl p-6" data-id="element-7045" data-mp="vffckg0fan20">
            <div className="text-center" data-id="element-7046" data-mp="109jnfact7f8d">
              <h2 className="text-xl font-medium text-white mb-2" data-id="element-7047" data-mp="lj2ihh0yu76q">
                QR Code Generator
              </h2>
              <p className="text-blue-300" data-id="element-7048" data-mp="qiovbpcvtlf0">
                Please select a fair first to generate a QR code
              </p>
            </div>
          </div>;
      case 'users':
        return <UsersManagement data-id="element-7049" data-mp="1j6kur1tu5rfh" />;
      case 'territory':
        return <TerritoryManagement data-id="element-7050" data-mp="1j3ykuvjh2zu3" />;
      case 'dashboard':
        return <AdminDashboard setActiveModule={setActiveModule} data-id="element-7051" data-mp="1488ygwadkkou" />;
      default:
        return <div className="flex items-center justify-center h-64 bg-white/5 backdrop-blur-sm rounded-xl p-6" data-id="element-7052" data-mp="1973p2kel3dji">
            <div className="text-center" data-id="element-7053" data-mp="3oyhu2mozsotz">
              <h2 className="text-xl font-medium text-white mb-2" data-id="element-7054" data-mp="3p1m1dsxr80ov">
                {activeModule.charAt(0).toUpperCase() + activeModule.slice(1)}{' '}
                Module
              </h2>
              <p className="text-blue-300" data-id="element-7055" data-mp="e1xnu8om2832">This module is under development</p>
            </div>
          </div>;
    }
  };
  return <div className="flex flex-col min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-950" data-id="element-7056" data-mp="4a1n0pv543v5">
      {/* Admin Header — Quotes V2 style top bar */}
      <header className="bg-gradient-to-r from-blue-950/80 via-indigo-950/60 to-purple-950/50 backdrop-blur-sm border-b border-white/10 px-6 relative z-10 flex-shrink-0 h-[72px] flex items-center" data-id="element-7057" data-mp="3viff99d1h3ss">
        {/* Breadcrumb navigation — left aligned */}
        <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 flex-shrink-0" data-id="element-7058" data-mp="mzzvv86ri0zu">
          <HomeIcon size={15} className="text-blue-300/60 hover:text-blue-200 cursor-pointer transition-colors flex-shrink-0" onClick={() => setActiveModule('dashboard')} data-id="element-7059" data-mp="i27tfk8hqwhe" />
          <ChevronRightIcon size={13} className="text-blue-300/40 flex-shrink-0" data-id="element-7060" data-mp="37onnkid6d9ug" />
          {activeModule === 'dashboard' ? <span className="text-sm font-semibold text-white" data-id="element-7061" data-mp="1ia67tnl6bknh">Admin</span> : <>
              <button onClick={() => setActiveModule('dashboard')} className="text-xs text-blue-300/60 hover:text-blue-200 transition-colors" data-id="element-7062" data-mp="1i81sm9htqz7w">
                Admin
              </button>
              <ChevronRightIcon size={13} className="text-blue-300/40 flex-shrink-0" data-id="element-7063" data-mp="1s5zg2urv0rzg" />
              <span className="text-sm font-semibold text-white" data-id="element-7064" data-mp="1x03ozjkora36">
                {moduleLabel(activeModule)}
              </span>
            </>}
        </nav>
      </header>
      {/* Main Content */}
      <div className="flex-1 overflow-y-auto p-6" data-id="element-7065" data-mp="25xnrevuabcfd">
        {activeModule === 'dashboard' && <>
            <div className="mb-6" data-id="element-7066" data-mp="2azd22adlho2x">
              <h1 className="text-2xl font-bold text-white" data-id="element-7067" data-mp="2fxemaymrbtfz">
                Administrative configurator
              </h1>
              <p className="text-blue-300 mt-1" data-id="element-7068" data-mp="glc8a3wwjr9p">
                Manage system settings, configurations, and administrative tasks
              </p>
            </div>
            {/* Product and Component Configuration */}
            <div className="mb-8" data-id="element-7069" data-mp="djpkkqehzxa6">
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center" data-id="element-7070" data-mp="dkr4lkdhru41">
                <PackageIcon size={20} className="mr-2 text-blue-400" data-id="element-7071" data-mp="9jfe7cmsn4us" />
                Product and Component Configuration
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-7072" data-mp="ihhka6ldwnox">
                <ConfigCard icon={<TableIcon size={24} data-id="element-7074" data-mp="wh0gq24u2ale" />} title="Matrix" description="Assignment of properties to sets and kits for validation and selection in designs." onClick={() => handleModuleChange('matrix')} data-id="element-7073" data-mp="whk0loe1n922" />
                <ConfigCard icon={<PackageIcon size={24} data-id="element-7076" data-mp="166a8fgvrlr8z" />} title="Items" description="Management of available items/products." onClick={() => handleModuleChange('items')} data-id="element-7075" data-mp="167v2l5e6386b" />
                <ConfigCard icon={<div className="w-6 h-6 flex items-center justify-center bg-blue-400/20 rounded text-lg font-bold" data-id="element-7078" data-mp="1g59jt77x00zo">
                      S
                    </div>} title="Span Config" description="Configuration of spans used in irrigation systems." onClick={() => handleModuleChange('span')} data-id="element-7077" data-mp="1g5ssryfl7f1k" />
                <ConfigCard icon={<UmbrellaIcon size={24} data-id="element-7080" data-mp="1yt1xzs8ha45p" />} title="Overhang Config" description="Configuration of overhangs for project designs." onClick={() => handleModuleChange('overhang')} data-id="element-7079" data-mp="1yulcpeehk75d" />
                <ConfigCard icon={<div className="w-6 h-6 flex items-center justify-center bg-blue-400/20 rounded text-lg font-bold" data-id="element-7082" data-mp="1psc4iv6owpv5">
                      S
                    </div>} title="Sprinkler Config" description="Configuration of sprinklers and their technical parameters." onClick={() => handleModuleChange('sprinkler')} data-id="element-7081" data-mp="1psvla8klt87l" />
              </div>
            </div>
            {/* Commercial and Logistics Setup */}
            <div className="mb-8" data-id="element-7083" data-mp="173yqm3laodou">
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center" data-id="element-7084" data-mp="170t3gtw87lvr">
                <GlobeIcon size={20} className="mr-2 text-green-400" data-id="element-7085" data-mp="s6eqb9zcf6p0" />
                Commercial and Logistics Setup
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-7086" data-mp="1cmh9gv1apji4">
                <ConfigCard icon={<GlobeIcon size={24} data-id="element-7088" data-mp="18lf92avn71ek" />} title="Region and Countries" description="Definition of regions and countries for geographic assignment." onClick={() => handleModuleChange('regions')} data-id="element-7087" data-mp="1ckelegjpe0g1" />
                <ConfigCard icon={<NetworkIcon size={24} data-id="element-7090" data-mp="ynhobxczrmqp" />} title="Territory Management" description="Manage regional access and default quote responsibilities." onClick={() => handleModuleChange('territory')} data-id="element-7089" data-mp="yn85ctqd6fui" />
                <ConfigCard icon={<ListIcon size={24} data-id="element-7092" data-mp="ow3qrv4lox07" />} title="Incoterm" description="Setup of international trade terms (FOB, CIF, etc.)." onClick={() => handleModuleChange('incoterm')} data-id="element-7091" data-mp="owmwmdgz7uxv" />
                <ConfigCard icon={<DollarSignIcon size={24} data-id="element-7094" data-mp="fx9njlwrl8do" />} title="Price List" description="Administration of price lists by region, customer, or product." onClick={() => handleModuleChange('pricelist')} data-id="element-7093" data-mp="fwqg4z4qq2dw" />
                <ConfigCard icon={<WarehouseIcon size={24} data-id="element-7096" data-mp="5zc030mqbfm4" />} title="Warehouse" description="Management of warehouses and stock locations." onClick={() => handleModuleChange('warehouse')} data-id="element-7095" data-mp="5xsldegq1cmg" />
                <ConfigCard icon={<CreditCardIcon size={24} data-id="element-7098" data-mp="snkppnymsy4" />} title="Payment Terms" description="Setup of payment conditions applicable to customers and quotations." onClick={() => handleModuleChange('payment')} data-id="element-7097" data-mp="sxfdqjy9oa5" />
                <ConfigCard icon={<TruckIcon size={24} data-id="element-7100" data-mp="aqblaw88yuo7" />} title="Type Of Transportation" description="Configuration of logistics and transport options." onClick={() => handleModuleChange('transportation')} data-id="element-7099" data-mp="arwfgkqngblj" />
              </div>
            </div>
            {/* Fairs and Promotions */}
            <div className="mb-8" data-id="element-7101" data-mp="3j6wizgvfi1c0">
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center" data-id="element-7102" data-mp="3j5v93fq5fkvv">
                <TentIcon size={20} className="mr-2 text-amber-400" data-id="element-7103" data-mp="3nyy4zuief6f8" />
                Fairs and Promotions
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-7104" data-mp="2qiinp8vq5j45">
                <ConfigCard icon={<TentIcon size={24} data-id="element-7106" data-mp="2vfr53791tj4c" />} title="Agricultural Fairs" description="Register and manage agricultural fairs participation." onClick={() => handleModuleChange('fairs')} highlight={true} data-id="element-7105" data-mp="2vganep2m29iw" />
                <ConfigCard icon={<QrCodeIcon size={24} data-id="element-7108" data-mp="ckdrpz7bv3km" />} title="QR Code Generator" description="Create promotional QR codes with automatic discounts." onClick={() => handleModuleChange('qrcode')} highlight={true} data-id="element-7107" data-mp="cknme03bhywn" />
              </div>
            </div>
            {/* Version Control and Automations */}
            <div className="mb-8" data-id="element-7109" data-mp="3kw9ru06gu1lh">
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center" data-id="element-7110" data-mp="3pwxs7cgl3usd">
                <SettingsIcon size={20} className="mr-2 text-purple-400" data-id="element-7111" data-mp="3tzavzvbrtimi" />
                Version Control and Automations
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-7112" data-mp="2l6h3jrz9n17">
                <ConfigCard icon={<ListIcon size={24} data-id="element-7114" data-mp="327d6ilt1qjfe" />} title="Version Management" description="Control and tracking of system versions and configurations." onClick={() => handleModuleChange('version')} data-id="element-7113" data-mp="326to73zhht0u" />
                <ConfigCard icon={<ClockIcon size={24} data-id="element-7116" data-mp="2ypfgigctm9q3" />} title="Cron Tasks" description="Scheduled tasks and system automations." onClick={() => handleModuleChange('cron')} data-id="element-7115" data-mp="2yont3stdniaw" />
              </div>
            </div>
            {/* Other Elements */}
            <div className="mb-8" data-id="element-7117" data-mp="2pr3nk7ohezuh">
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center" data-id="element-7118" data-mp="2kqz72hnkq549">
                <FileTextIcon size={20} className="mr-2 text-blue-400" data-id="element-7119" data-mp="2frmnc5s01e5w" />
                Other Elements
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-7120" data-mp="2azbjiru51882">
                <ConfigCard icon={<MessagesSquareIcon size={24} data-id="element-7122" data-mp="262324tgtd87v" />} title="Feedback" description="Review and management of user comments or system feedback." onClick={() => handleModuleChange('feedback')} data-id="element-7121" data-mp="260i9j9e27zcn" />
                <ConfigCard icon={<ShieldIcon size={24} data-id="element-7124" data-mp="1x0261zxbgs7s" />} title="iControl Remote" description="Configuration of integration with remote irrigation control systems." onClick={() => handleModuleChange('icontrol')} data-id="element-7123" data-mp="1wzk02bkuqzmo" />
                <ConfigCard icon={<UsersIcon size={24} data-id="element-7126" data-mp="1iclf8zgi33gx" />} title="Users" description="User account management and access control." onClick={() => handleModuleChange('users')} data-id="element-7125" data-mp="1icbkkykig84w" />
              </div>
            </div>
          </>}
        {/* Show module content when a module is selected */}
        {activeModule !== 'dashboard' && <div data-id="element-7127" data-mp="1dgw92d9z6obn">{renderModuleContent()}</div>}
      </div>
    </div>;
}
function ConfigCard({
  icon,
  title,
  description,
  onClick,
  highlight = false
}) {
  return <button onClick={onClick} className={`bg-white/5 backdrop-blur-sm border ${highlight ? 'border-amber-500/50 ring-1 ring-amber-500/30' : 'border-white/10'} rounded-xl p-5 flex flex-col items-start gap-3 hover:bg-white/10 transition-colors text-left h-full group`} data-id="element-7128" data-mp="262fn12lyq1ub">
      <div className={`p-2.5 rounded-lg ${highlight ? 'bg-amber-500/10 text-amber-400' : 'bg-white/5 text-blue-400 group-hover:bg-blue-500/20 group-hover:text-blue-300 transition-colors'}`} data-id="element-7129" data-mp="2k3iddrjzpyb1">
        {icon}
      </div>
      <div data-id="element-7130" data-mp="de7yz399tngv">
        <span className="font-semibold text-white block mb-1" data-id="element-7131" data-mp="ddy87dgejca4">{title}</span>
        <p className="text-xs text-blue-300/70 leading-relaxed" data-id="element-7132" data-mp="8get6bbvqvh4">
          {description}
        </p>
      </div>
    </button>;
}
function AdminDashboard({
  setActiveModule
}) {
  const stats = [{
    title: 'Total Users',
    value: 248,
    change: '+12%',
    trend: 'up',
    icon: <UsersIcon size={20} className="text-blue-400" data-id="element-7133" data-mp="3qli3cagpdxoe" />
  }, {
    title: 'Active Dealers',
    value: 86,
    change: '+5%',
    trend: 'up',
    icon: <BuildingIcon size={20} className="text-emerald-400" data-id="element-7134" data-mp="3lnqc7invufl9" />
  }, {
    title: 'Products',
    value: 1254,
    change: '+3%',
    trend: 'up',
    icon: <PackageIcon size={20} className="text-amber-400" data-id="element-7135" data-mp="3eh6f749tijf" />
  }, {
    title: 'Upcoming Fairs',
    value: 5,
    change: 'New',
    trend: 'none',
    icon: <TentIcon size={20} className="text-purple-400" data-id="element-7136" data-mp="1eunvhbqvbq02" />
  }];
  const quickActions = [{
    title: 'Register Fair',
    description: 'Add a new agricultural fair',
    icon: <TentIcon size={20} className="text-amber-400" data-id="element-7137" data-mp="264ttbfit4gni" />,
    module: 'fairs'
  }, {
    title: 'Generate QR Code',
    description: 'Create promotional QR code',
    icon: <QrCodeIcon size={20} className="text-purple-400" data-id="element-7138" data-mp="2a888k3yx8szw" />,
    module: 'qrcode'
  }, {
    title: 'Manage Price Lists',
    description: 'Update product pricing',
    icon: <DollarSignIcon size={20} className="text-emerald-400" data-id="element-7139" data-mp="2k54jshg7ft85" />,
    module: 'pricelist'
  }, {
    title: 'Configure Products',
    description: 'Manage product catalog',
    icon: <PackageIcon size={20} className="text-blue-400" data-id="element-7140" data-mp="2t4hwrk3jezxm" />,
    module: 'items'
  }];
  return <div className="space-y-6" data-id="element-7141" data-mp="2xvzxc71neyi2">
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-7142" data-mp="2xxt3zmc9hiq2">
        {stats.map((stat, index) => <div key={index} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-5" data-id="element-7143" data-mp="32wx9a1w2ewb4" data-mpi={index}>
            <div className="flex justify-between items-start mb-2" data-id="element-7144" data-mp="37s2x56912l1u">
              <div className="p-2 bg-white/5 rounded-lg" data-id="element-7145" data-mp="3ctifurnqgfh2">{stat.icon}</div>
              <div className="mt-1 text-xs font-medium" data-id="element-7146" data-mp="116oqpdfiy9zp">
                {stat.trend === 'up' && <span className="text-emerald-400 flex items-center" data-id="element-7147" data-mp="w612t0mle99p">
                    <TrendingUpIcon size={14} className="mr-1" data-id="element-7148" data-mp="w8wysjevqnvi" />
                    {stat.change}
                  </span>}
                {stat.trend === 'down' && <span className="text-red-400 flex items-center" data-id="element-7149" data-mp="m9fv27j6uk26">
                    <TrendingUpIcon size={14} className="mr-1 rotate-180" data-id="element-7150" data-mp="1jvvcj9bm7hpq" />
                    {stat.change}
                  </span>}
                {stat.trend === 'none' && <span className="text-amber-400 flex items-center" data-id="element-7151" data-mp="1extsom6d7ed7">
                    {stat.change}
                  </span>}
              </div>
            </div>
            <h3 className="text-2xl font-bold text-white mb-1" data-id="element-7152" data-mp="1567bm97cil2j">
              {stat.value.toLocaleString()}
            </h3>
            <p className="text-sm text-blue-300/70" data-id="element-7153" data-mp="22o7c5oavw4w6">{stat.title}</p>
          </div>)}
      </div>
      {/* Quick Actions */}
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-5" data-id="element-7154" data-mp="1xu35751mbwv3">
        <h3 className="text-lg font-semibold text-white mb-4" data-id="element-7155" data-mp="3s5ut2wosgmf8">Quick Actions</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-7156" data-mp="wxgkptrom0e">
          {quickActions.map((action, index) => <button key={index} onClick={() => setActiveModule(action.module)} className="bg-white/5 border border-white/5 rounded-xl p-4 text-left transition-colors hover:bg-white/10 flex flex-col h-full group" data-id="element-7157" data-mp="3ibuhcbz04ixj" data-mpi={index}>
              <div className="p-2.5 bg-white/5 rounded-lg inline-flex mb-3 group-hover:bg-white/10 transition-colors" data-id="element-7158" data-mp="39fmumrw1ak5d">
                {action.icon}
              </div>
              <h3 className="text-sm font-semibold text-white mb-1" data-id="element-7159" data-mp="2zgnkt5zj8igs">
                {action.title}
              </h3>
              <p className="text-xs text-blue-300/70 mt-1 leading-relaxed" data-id="element-7160" data-mp="34g9zljv0e6rp">
                {action.description}
              </p>
              <div className="mt-4 text-xs font-medium text-blue-400 flex items-center mt-auto opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-7161" data-mp="12mkhsw254epg">
                Access
                <ArrowRightIcon size={12} className="ml-1" data-id="element-7162" data-mp="2n2ihg728bmbz" />
              </div>
            </button>)}
        </div>
      </div>
    </div>;
}